/**
 * Application entry point.
 *
 * The whole site is one document; language is the only route. Switching it
 * re-renders the page, rewrites the URL and updates the document head, without
 * a network round trip.
 */

import './styles/tokens.css';
import './styles/app.css';

import { CONTENT, DEFAULT_LANG, LANGS, LANG_PATH } from './content.js';
import { renderPage } from './components/page.js';
import { mount } from './lib/dom.js';
import { initialLang, langFromPath, onRouteChange, pushLang, rememberLang } from './lib/router.js';

const root = document.getElementById('app');
let current = null;

function render(lang, { push = false } = {}) {
  if (!LANGS.includes(lang)) lang = DEFAULT_LANG;
  if (lang === current) return;

  const y = window.scrollY;
  current = lang;

  mount(root, renderPage(lang, { onLangChange: switchTo }));
  updateHead(lang);
  if (push) pushLang(lang);

  // A language switch is not a navigation — hold the reader where they were.
  window.scrollTo(0, y);
}

function switchTo(lang) {
  rememberLang(lang);
  render(lang, { push: true });
}

/**
 * Keep the head in step with the rendered language: crawlers, share cards and
 * screen readers all read it, and none of them see the DOM we just built.
 */
function updateHead(lang) {
  const c = CONTENT[lang];
  const url = new URL(LANG_PATH[lang], location.origin).href;

  document.documentElement.lang = c.htmlLang;
  document.title = c.meta.title;

  setMeta('name', 'description', c.meta.description);
  setMeta('property', 'og:title', c.meta.title);
  setMeta('property', 'og:description', c.meta.description);
  setMeta('property', 'og:locale', c.htmlLang.replace('-', '_'));
  setMeta('property', 'og:url', url);

  setLink('canonical', url);
  for (const l of LANGS) setLink('alternate', new URL(LANG_PATH[l], location.origin).href, l);
  setLink('alternate', new URL('/', location.origin).href, 'x-default');
}

function setMeta(attr, key, value) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.append(tag);
  }
  tag.setAttribute('content', value);
}

function setLink(rel, href, hreflang = null) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    if (hreflang) tag.hreflang = hreflang;
    document.head.append(tag);
  }
  tag.href = href;
}

render(initialLang());
onRouteChange((lang) => render(lang));

// A first visit to "/" that the browser or a past visit says should be read in
// another language: move the URL to match, replacing rather than pushing so the
// back button still leaves the site.
if (!langFromPath() && current !== DEFAULT_LANG) {
  history.replaceState({ lang: current }, '', LANG_PATH[current] + location.hash);
}

// The hash is resolved before the page exists, so honour it once it does.
if (location.hash) {
  document.getElementById(location.hash.slice(1))?.scrollIntoView();
}
